import { createContext, useContext, useEffect, useState } from "react";
import { PremiumPubPage, usePubTab } from "./use-pub-tab";
import { usePubDetail } from "./use-pub-detail";
import {
  Cafe,
  CurrentReservation,
  PubTournament,
  ReviewDataResponse,
} from "../../../../api/types";
import { YoutubeLiveDto } from "../../../../api/youtube-live";
import { calculateTimes, calculateTimeAtStart } from "../../../../utils/timer";
import { currentTimeState } from "../../../../recoil/app";
import { useRecoilValue } from "recoil";
import { enqueueSnackbar } from "notistack";
import { reservationCancel } from "../../../../api/reservation";
import { cancelCompanyReservation } from "../../../../api/company-reservation";
import { isApp } from "../../../../hooks/useNativeApp";
import useScreenOrientation from "../../../../hooks/useScreenOrientation";

interface PubContextType {
  pubPage: PremiumPubPage;
  setPubPage: (v: PremiumPubPage) => void;
  isPubPage: (v: PremiumPubPage) => boolean;
  setPubId: (v: number) => void;
  data: Cafe | null;
  liveList: YoutubeLiveDto[];
  loading: boolean;
  showHeader: boolean;
  setShowHeader: (v: boolean) => void;
  isReservationCancel: boolean;
  setIsReservationCancel: (v: boolean) => void;
  tournamentList: PubTournament[];
  handleCancel: () => void;
  setReservationCancelItem: (v: CurrentReservation) => void;
  isOpenUseApp: boolean;
  setIsOpenUseApp: (v: boolean) => void;
  goToApp: () => void;
  showWrite: boolean;
  setShowWrite: (v: boolean) => void;
  getData: () => void;
  editReview: ReviewDataResponse | null;
  setEditReview: (v: ReviewDataResponse | null) => void;
}

const PubContext = createContext<PubContextType | null>(null);

export const PubProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const { pubPage, setPubPage, isPubPage } = usePubTab();
  const currentTime = useRecoilValue(currentTimeState);
  const orientation = useScreenOrientation();
  const [pubId, setPubId] = useState(-1);
  const { data, liveList, loading, getData } = usePubDetail(pubId);
  const [showHeader, setShowHeader] = useState(true);
  const [showWrite, setShowWrite] = useState(false);
  const [editReview, setEditReview] = useState<ReviewDataResponse | null>(null);
  const [isReservationCancel, setIsReservationCancel] = useState(false);
  const [tournamentList, setTournamentList] = useState<PubTournament[]>([]);
  const [reservationCancelItem, setReservationCancelItem] =
    useState<CurrentReservation>();
  const [isOpenUseApp, setIsOpenUseApp] = useState(false);

  useEffect(() => {
    if (!data) return;

    const list = [];
    const onTournamentList = data.pubTournamentList;
    for (var tournament of onTournamentList) {
      if (tournament.timerDto && tournament.structure.length > 0) {
        const time = calculateTimes(tournament.timerDto, tournament.structure);
        if (time && time.overStack < 1) {
          tournament.timeInfo = time;
          list.push(tournament);
        }
      } else if (tournament.structure.length > 0) {
        const startDate = new Date(tournament.startDate);
        const time = calculateTimeAtStart(
          startDate,
          new Date(tournament.createdAt),
          tournament.structure[0],
          tournament.structure[1]
        );
        tournament = { ...tournament, timeInfo: time };
        if (
          tournament.timeInfo.remainingTimeInCurrentLevel.indexOf("NaN") === -1
        )
          list.push(tournament);
      }
    }
    setTournamentList(list);
  }, [data, currentTime]);

  // 래빗 페스티벌 3일 전 취소 불가
  const handleCanRunnerReservationCancel = () => {
    if (!reservationCancelItem) return;
    const startDate = new Date(reservationCancelItem.tournament.startDate);
    const currentDate = new Date();
    currentDate.setHours(0, 0, 0, 0);

    const startDateOnly = new Date(startDate);
    startDateOnly.setHours(0, 0, 0, 0);

    const dDayRemaining = Math.ceil(
      (startDateOnly.getTime() - currentDate.getTime()) / (1000 * 60 * 60 * 24)
    );
    if (dDayRemaining <= 3) {
      enqueueSnackbar("대회 시작 3일 전에는 예약을 취소할 수 없습니다. ", {
        variant: "error",
      });
      setIsReservationCancel(false);
      return false;
    }
    return true;
  };

  // 예약 취소 로직
  const handleCancel = async () => {
    if (!reservationCancelItem || !data) return;
    try {
      if (data.pubType === "RUNNER") {
        if (!handleCanRunnerReservationCancel()) return;
        await cancelCompanyReservation(reservationCancelItem.id);
      } else {
        await reservationCancel(reservationCancelItem.id);
      }
      enqueueSnackbar("해당 토너먼트 예약 취소되었습니다. ", {
        variant: "success",
      });
      setIsReservationCancel(false);
      getData();
    } catch (e: any) {
      enqueueSnackbar(e.message, {
        variant: "error",
      });
      setIsReservationCancel(false);
      console.error(e);
    }
  };

  useEffect(() => {
    // URL에서 store_redirect 파라미터 확인
    const params = new URLSearchParams(window.location.search);
    const shouldRedirect = params.get("store_redirect");

    if (shouldRedirect === "true" && !isApp) {
      const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
      if (isIOS) {
        window.location.href = "https://apps.apple.com/app/6469495456";
      } else {
        window.location.href =
          "https://play.google.com/store/apps/details?id=com.runnersoft.runnerrunner";
      }
    }
  }, []);

  const goToApp = () => {
    if (!isApp) {
      const isKakao = /KAKAOTALK/i.test(navigator.userAgent);
      const redirectUrl =
        window.location.href +
        (window.location.search ? "&" : "?") +
        "store_redirect=true";
      // 앱 실행 시도
      if (isKakao) {
        enqueueSnackbar(redirectUrl);
        window.location.href =
          "kakaotalk://web/openExternal?url=" + encodeURIComponent(redirectUrl);
      } else {
        const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
        if (isIOS) {
          const link = document.createElement("a");
          link.href = redirectUrl;
          link.style.display = "none";
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        } else {
          window.open(redirectUrl, "_blank");
          window.close();
        }
      }
      return;
    }
  };

  useEffect(() => {
    if (!isApp && orientation !== "landscape") {
      setIsOpenUseApp(true);
    }
  }, []);

  const value = {
    // 텝
    pubPage,
    setPubPage,
    isPubPage,
    // 데이터 불러올 펍 id
    setPubId,
    // 데이터
    data,
    liveList,
    tournamentList,
    // 로딩
    loading,
    // 헤더
    showHeader,
    setShowHeader,
    // 예약 취소 팝업
    isReservationCancel,
    setIsReservationCancel,
    // 예약 취소
    handleCancel,
    // 예약 취소 대상
    setReservationCancelItem,
    // 모바일 페이지 이용 여부
    isOpenUseApp,
    setIsOpenUseApp,
    goToApp,
    // 리뷰 작성 페이지
    showWrite,
    setShowWrite,
    editReview,
    setEditReview,
    // 데이터 호출
    getData,
  };

  return <PubContext.Provider value={value}>{children}</PubContext.Provider>;
};

export const usePubContext = () => {
  const context = useContext(PubContext);
  if (!context) {
    throw new Error("usePubContext must be used within a PubProvider");
  }
  return context;
};
